/**
 * SNMP: what the matching monitor knows about this same machine.
 *
 * **The tab exists only while a monitor matches.** A machine can be in the CRM twice - enrolled as an
 * agent device and adopted as an SNMP monitor - and the server joins the two records by NIC MAC, then
 * IP, then unit serial. When it finds a match, this is that machine's SNMP half: its latest polled
 * readings, the same performance graphs the monitor's own page shows, and a probe.
 *
 * The probe is deliberately of the settings **as stored**: there is no edit form here, so an empty
 * request is the only sensible question, and it is exactly the question this tab raises - a
 * technician lands here when the SNMP half of a twice-enrolled device has gone quiet. Editing them
 * is the monitor page's job, which is one click away.
 *
 * The performance card is independent of the Performance tab's. That one is what the agent reports
 * about the operating system; this one is what the device reports about itself over SNMP, and on a
 * NAS or a router the two are genuinely different views of the machine.
 */
import * as crm from '../../api/crm.js';
import { describeError } from '../../api/http.js';
import type { SnmpMonitorDto } from '../../contracts.gen.js';
import { join } from '../../format.js';
import { link } from '../../ui/Link.js';
import { StatusLine } from '../../ui/StatusLine.js';
import {
  button,
  buttonRow,
  card,
  fillWith,
  FONT,
  GAP,
  h2,
  muted,
  splitRow,
  stackOf,
  value,
  vstack
} from '../../ui/layout.js';
import { fillMetricGroups } from '../sections/metric-groups.js';
import { monitorPerformanceSection } from '../sections/monitor-performance.js';
import type { DeviceTab, DeviceTabContext } from './context.js';

export interface SnmpTab extends DeviceTab {
  /** Points the tab at the monitor the server joined to this device, or hides it when there is none. */
  apply(monitor: SnmpMonitorDto | null): Promise<void>;
  /** Releases the monitor watch. The shell calls this on unmount. */
  detach(): Promise<void>;
}

export function snmpTab(dc: DeviceTabContext): SnmpTab {
  let monitor: SnmpMonitorDto | null = null;
  let probing = false;

  const panel = vstack({ spacing: GAP.md, hAlign: 'stretch', autoWidth: false });
  const probeStatus = new StatusLine();

  // ---- header ----
  const statusLine = muted('');
  const testButton = button('Test connection', () => void probe());
  const openLink = link('Open monitored device →', () => {
    if (monitor) dc.ctx.go(`/monitors/${monitor.id}`);
  });

  const headerRow = splitRow({ align: 'center' });
  fillWith(
    headerRow,
    stackOf(
      vstack({ spacing: GAP.xs }),
      value('Monitored over SNMP', { font: FONT.bold }),
      statusLine
    )
  );
  headerRow.controls.add(stackOf(buttonRow({ hAlign: 'end' }), testButton, openLink));

  // Each channel reports separately, so "SNMP fine, admin password wrong" reads as such instead of
  // one verdict covering both.
  const snmpLine = value('', { font: FONT.small, wrap: true });
  const apiLine = value('', { font: FONT.small, wrap: true });
  snmpLine.visible = false;
  apiLine.visible = false;

  const headerCard = card();
  headerCard.controls.add(
    stackOf(
      vstack({ spacing: GAP.sm, hAlign: 'stretch', autoWidth: false }),
      headerRow,
      snmpLine,
      apiLine,
      probeStatus.label
    )
  );

  // ---- performance ----
  const performance = monitorPerformanceSection({
    desktop: dc.ctx.desktop,
    dispose: dc.ctx.dispose,
    cancelled: () => dc.ctx.cancelled(),
    relayout: () => dc.relayout()
  });

  // ---- latest readings, grouped ----
  const groupHost = vstack({ spacing: GAP.md, hAlign: 'stretch', autoWidth: false });
  const noMetrics = muted('No metrics yet — waiting for the first poll.');

  panel.controls.addRange([
    headerCard,
    h2('SNMP performance'),
    performance.panel,
    groupHost,
    noMetrics
  ]);

  function applyHeader(): void {
    if (!monitor) {
      statusLine.text = '';
      return;
    }
    statusLine.text = join([
      monitor.kindLabel,
      monitor.ipAddress,
      `SNMP ${monitor.snmpVersionLabel}`,
      monitor.statusLabel,
      monitor.lastPolledLabel ? `last polled ${monitor.lastPolledLabel}` : null
    ]);
  }

  function applyGroups(): void {
    const metrics = monitor?.latestMetrics ?? [];
    fillMetricGroups(groupHost, metrics);
    noMetrics.visible = monitor !== null && metrics.length === 0;
  }

  async function probe(): Promise<void> {
    if (!monitor || probing) return;
    probing = true;
    testButton.enabled = false;
    probeStatus.busy('Probing…');
    try {
      const result = await crm.testMonitor(monitor.id, {});
      if (dc.ctx.cancelled()) return;
      snmpLine.text =
        (result.snmpOk ? 'SNMP OK — ' : 'SNMP failed — ') +
        result.snmpMessage +
        (result.detail ? `  (${result.detail})` : '');
      snmpLine.visible = true;
      // Empty unless this is a Synology with a Web API login: there is no second channel otherwise,
      // and an empty line implying one had failed would send a technician after a problem that does
      // not exist.
      apiLine.text = result.apiChecked
        ? (result.apiOk ? 'Web API OK — ' : 'Web API failed — ') + result.apiMessage
        : '';
      apiLine.visible = apiLine.text.length > 0;
      // The failure summary points at the line above rather than blaming the credentials: an
      // unreachable agent - or an unreachable CRM - is not a settings problem, and sending somebody
      // to re-type a working community string is a wasted half-hour.
      probeStatus.set(
        result.anyOk
          ? 'The device answered — the stored settings work.'
          : 'No answer — see the reason above.',
        result.anyOk ? 'ok' : 'error'
      );
    } catch (error) {
      if (!dc.ctx.cancelled()) probeStatus.error(describeError(error));
    } finally {
      probing = false;
      testButton.enabled = monitor !== null;
      dc.relayout();
    }
  }

  return {
    panel,
    async apply(next) {
      monitor = next;
      testButton.enabled = next !== null;
      applyHeader();
      applyGroups();
      // The probe's verdict is deliberately NOT cleared here. This runs on every `MonitorChanged`
      // push, which fires for any of the customer's monitors on every poll report, and wiping the
      // answer out from under somebody reading it would be worse than letting it age.
      if (next) await performance.attach(next);
      else await performance.detach();
      dc.relayout();
    },
    detach() {
      return performance.detach();
    }
  };
}
